import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Avatar, Box, Chip, IconButton, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Gem } from "lucide-react";
import { formatTimestamp } from "utils";
import { hasPremiumData } from "../clip-ui/clip-menu";
import { ClipConfiguration, Status } from "../clip-ui/models";
import { getStatusChipColor } from "../clip-utils";

interface GetColumnsParams {
  handleMenuOpen: (
    event: React.MouseEvent<HTMLElement>,
    clip: ClipConfiguration
  ) => void;
}

/**
 * Returns the column definitions for the clip management table
 */
export const getClipManagementColumns = ({
  handleMenuOpen,
}: GetColumnsParams): GridColDef[] => [
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 200,
    renderCell: (params) => (
      <Box display="flex" alignItems="center" height="100%">
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      </Box>
    ),
  },
  {
    field: "clientReference",
    headerName: "Client Ref",
    flex: 0.8,
    minWidth: 150,
    renderCell: (params) => (
      <Box display="flex" alignItems="center" height="100%">
        <Typography variant="body2" noWrap>
          {params.value || "-"}
        </Typography>
      </Box>
    ),
  },
  {
    field: "totalCredits",
    headerName: "Credits",
    width: 100,
    renderCell: (params) => {
      const clip = params.row as ClipConfiguration;
      return (
        <Box display="flex" alignItems="center" height="100%">
          <Typography
            variant="body2"
            color={clip.totalCredits ? "text.primary" : "text.secondary"}
          >
            {clip.totalCredits ? clip.totalCredits.toLocaleString() : "-"}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: "totalDollars",
    headerName: "Cost ($)",
    width: 100,
    renderCell: (params) => {
      const clip = params.row as ClipConfiguration;
      return (
        <Box display="flex" alignItems="center" height="100%">
          <Typography
            variant="body2"
            color={clip.totalDollars ? "text.primary" : "text.secondary"}
          >
            {clip.totalDollars
              ? `$${clip.totalDollars.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : "-"}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    renderCell: (params) => (
      <Box display="flex" alignItems="center" height="100%">
        <Chip
          label={params.value}
          color={getStatusChipColor(params.value as Status)}
          size="small"
          variant="outlined"
        />
      </Box>
    ),
  },
  {
    field: "premium",
    headerName: "Premium",
    width: 100,
    sortable: false,
    renderCell: (params) => {
      const clip = params.row as ClipConfiguration;
      const isPremium = hasPremiumData(clip);
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          {isPremium && <Gem size={18} color="#1976d2" />}
        </Box>
      );
    },
  },
  {
    field: "author",
    headerName: "Author",
    flex: 1,
    minWidth: 150,
    renderCell: (params) => {
      const clip = params.row as ClipConfiguration;
      const author = clip.authorName || clip.developer || "";
      return (
        <Box display="flex" alignItems="center" gap={1} height="100%">
          {author && (
            <>
              <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                {author.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="body2" noWrap>
                {author}
              </Typography>
            </>
          )}
        </Box>
      );
    },
  },
  {
    field: "lastEditedAt",
    headerName: "Modified",
    width: 100,
    renderCell: (params) => {
      const clip = params.row as ClipConfiguration;
      const timestamp = clip.lastEditedAt
        ? clip.lastEditedAt * 1000
        : new Date(clip.date).getTime();
      return (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2">{formatTimestamp(timestamp)}</Typography>
        </Box>
      );
    },
  },
  {
    field: "purchasedOn",
    headerName: "Purchased",
    width: 100,
    sortComparator: (_v1, _v2, param1, param2) => {
      const clip1 = param1.api.getRow(param1.id) as ClipConfiguration;
      const clip2 = param2.api.getRow(param2.id) as ClipConfiguration;
      const timestamp1 = clip1.purchasedOn
        ? clip1.purchasedOn * 1000
        : clip1.status === "Completed"
        ? new Date(clip1.date).getTime()
        : 0;
      const timestamp2 = clip2.purchasedOn
        ? clip2.purchasedOn * 1000
        : clip2.status === "Completed"
        ? new Date(clip2.date).getTime()
        : 0;
      return timestamp1 - timestamp2;
    },
    renderCell: (params) => {
      const clip = params.row as ClipConfiguration;
      const timestamp = clip.purchasedOn
        ? clip.purchasedOn * 1000
        : clip.status === "Completed"
        ? new Date(clip.date).getTime()
        : null;
      return (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2" color={timestamp ? "text.primary" : "text.secondary"}>
            {timestamp ? formatTimestamp(timestamp) : "-"}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: "completedOn",
    headerName: "Completed",
    width: 100,
    sortComparator: (_v1, _v2, param1, param2) => {
      const clip1 = param1.api.getRow(param1.id) as ClipConfiguration;
      const clip2 = param2.api.getRow(param2.id) as ClipConfiguration;
      const timestamp1 = clip1.completedOn
        ? clip1.completedOn * 1000
        : clip1.status === "Completed"
        ? new Date(clip1.date).getTime()
        : 0;
      const timestamp2 = clip2.completedOn
        ? clip2.completedOn * 1000
        : clip2.status === "Completed"
        ? new Date(clip2.date).getTime()
        : 0;
      return timestamp1 - timestamp2;
    },
    renderCell: (params) => {
      const clip = params.row as ClipConfiguration;
      const timestamp = clip.completedOn
        ? clip.completedOn * 1000
        : clip.status === "Completed"
        ? new Date(clip.date).getTime()
        : null;
      return (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2" color={timestamp ? "text.primary" : "text.secondary"}>
            {timestamp ? formatTimestamp(timestamp) : "-"}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: "actions",
    headerName: "",
    width: 60,
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" alignItems="center" height="100%">
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row as ClipConfiguration)}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
    ),
  },
];
